import { Inject, Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { FhInputConfigModel } from '@fh-client-interaction-frontends/input-config';
import { FhFormValidators } from '../../object/fh-form-validators';
import { APP_CONFIG, EnvironmentModel } from '@fh-client-interaction-frontends/fh-app-config';

@Pipe({
  name: 'fhInputConfig'
})
export class FhInputConfigPipe implements PipeTransform {
  appConfig: EnvironmentModel = new EnvironmentModel();
  config: FhInputConfigModel = new FhInputConfigModel();

  constructor(@Inject(APP_CONFIG) appConfigTemp: EnvironmentModel, private translateService: TranslateService) {
    this.appConfig = appConfigTemp;
    this.translateService.onLangChange.subscribe(() => {
      if (this.config.label) {
        this.config.label = this.translateService.instant(this.config.labelKey!) + (this.config.required ? ' *' : '');
      }
    });
  }

  transform(
    formControls: any,
    formControlName: string,
    inputType?: 'TEXT' | 'NUMBER' | 'INPUTMASK' | 'SWITCH' | 'TEXTAREA' | 'PASSWORD' | 'DROPDOWN' | 'DATE' | 'FORM_ARRAY' | 'FILE' | 'MULTISELECT',
    customLabel?: string,
    customLabelTranslate?: boolean,
    showPlaceholder?: boolean,
    customPlaceholder?: string,
    readOnly?: boolean,
    validators?: any,
    allowHtml?: boolean,
    customTooltip?: string,
    customTooltipTranslate?: boolean,
    prefix?:string,
    numberMode?:string,
    minFractionDigits?: number,
    maxFractionDigits?:number

  ): FhInputConfigModel {
    let label: string = '';
    let labelKey: string = '';
    let placeholder: string = '';
    let required: boolean = false;

    if(showPlaceholder == undefined) {
      showPlaceholder = true;
    }

    if (validators) {
      const control = formControls[formControlName];

      if (control) {
        const existingValidators = control.validator ? [control.validator] : [];

        const newValidators = validators
          .map((v: any) => {
            if (v.validatorName === 'required') {
              return FhFormValidators.required(v.params?.field);
            } else if (v.validatorName === 'nameValidator') {
              return FhFormValidators.nameValidator(v.params?.field);
            } else if (v.validatorName === 'isNumber') {
              return FhFormValidators.isNumber(v.params?.field);
            } else if (v.validatorName === 'isLocation') {
              return FhFormValidators.isLocation(v.params?.field);
            } else if (v.validatorName === 'isPostalCode') {
              return FhFormValidators.isPostalCode(v.params?.field);
            } else if (v.validatorName === 'emailValidator') {
              return FhFormValidators.emailValidator(v.params?.field);
            } else if (v.validatorName === 'personalIdentificationNumber') {
              return FhFormValidators.personalIdentificationNumber(v.params?.field);
            } else if (v.validatorName === 'isValidUrl') {
              return FhFormValidators.isValidUrl(v.params?.field);
            } else if (v.validatorName === 'phoneValidator') {
              return FhFormValidators.phoneValidator(v.params?.field, this.appConfig.defaults.phoneLength, this.appConfig.defaults.prefixRestrictions);
            } else if (v.validatorName === 'phoneValidatorByCountry') {
              return FhFormValidators.phoneValidatorByCountry(
                v.params?.phoneCodeField,
                v.params?.field,
                this.appConfig.defaults.countryCode,
                this.appConfig.defaults.phoneLength,
                this.appConfig.defaults.prefixRestrictions
              );
            } else if (v.validatorName === 'taxNumberValidator') {
              return FhFormValidators.taxNumberValidator(v.params?.fieldCountryCode, v.params?.field, v.params?.type);
            }

            return undefined;
          })
          .filter(Boolean);

        const combinedValidators = [...existingValidators, ...newValidators];

        control.setValidators(combinedValidators);
        control.updateValueAndValidity();
      }

      required = validators?.filter((item: any) => item.validatorName === 'required').length > 0;

      if (!required) {
        required = validators?.filter((item: any) => item.name === 'required').length > 0;
      }

      this.config.required = required;
    } else {
      required = formControls[formControlName]?._rawValidators?.filter((item: any) => item.validatorName === 'required').length > 0;
      if (!required) {
        required = formControls[formControlName]?._rawValidators?.filter((item: any) => item.name === 'required').length > 0;
      }

      this.config.required = required;
    }

    if (customLabel && customLabelTranslate) {
      label = this.translateService.instant(customLabel) + (required ? ' *' : '');
      labelKey = customLabel;
    } else if (customLabel && !customLabelTranslate) {
      label = customLabel + (required ? ' *' : '');
      labelKey = customLabel;
    } else if (!customLabel) {
      label = this.translateService.instant('fields.' + formControlName.charAt(0).toLowerCase() + formControlName.slice(1) + '.label') + (required ? ' *' : '');
      labelKey = 'fields.' + formControlName.charAt(0).toLowerCase() + formControlName.slice(1) + '.label';
    }

    if (showPlaceholder) {
      const keyPlaceholder = 'fields.' + formControlName.charAt(0).toLowerCase() + formControlName.slice(1) + '.placeholder';
      placeholder = this.translateService.instant(keyPlaceholder);

      if (placeholder === keyPlaceholder) {
        placeholder = '';
      }
    }
    if (showPlaceholder && customPlaceholder && customPlaceholder?.length > 0) {
      placeholder = this.translateService.instant(customPlaceholder);
    }

    let infoLabel = this.translateService.instant('fields.' + formControlName.charAt(0).toLowerCase() + formControlName.slice(1) + '.infoLabel');

    if (customTooltip && !customTooltipTranslate) {
      infoLabel = this.translateService.instant(customTooltip);
    } else if (customLabel && customLabelTranslate) {
      infoLabel = customTooltip;
    }

    if (infoLabel === 'fields.' + formControlName.charAt(0).toLowerCase() + formControlName.slice(1) + '.infoLabel') {
      infoLabel = '';
    }
    if (customTooltip && infoLabel === customTooltip) {
      infoLabel = '';
    }

    // const config: FhInputConfigModel = new FhInputConfigModel();
    this.config.label = label;
    this.config.labelKey = labelKey;
    this.config.infoLabel = infoLabel;
    this.config.placeholder = placeholder;
    this.config.type = inputType ?? 'TEXT';
    this.config.control = formControls[formControlName];
    this.config.readonly = readOnly;
    this.config.disabled = formControls[formControlName]?.disabled;
    this.config.allowHtml = allowHtml ?? false;
    this.config.prefix = prefix;
    this.config.numberMode = numberMode;
    this.config.minFractionDigits = minFractionDigits;
    this.config.maxFractionDigits = maxFractionDigits;
    return this.config;
  }
}
